import React from "react";
import { useState, useEffect, useContext, useRef } from 'react'
import { getAuth } from "firebase/auth";
import { get, ref, update } from "firebase/database";
import { db } from '../utils/firebaseApp.js';
import app from "../utils/firebaseApp.js";
import { AuthContext } from "../utils/auth.js";
import MenuBar from '../components/MenuBar.js';
import { BarChart, DonutChart } from "@tremor/react";
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ArrowRightIcon } from '@heroicons/react/20/solid'
import { Table, TableRow, TableBody, TableCell, Text } from "@tremor/react";
import TopBar from "../components/TopBar.js";
import ModalForYoutubeVideo from '../components/utils/ModalForYoutubeVideo.js';
import posthog from 'posthog-js';
import 'react-loading-skeleton/dist/skeleton.css';
import DevSkeleton from '../components/DevSkeleton.js';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Dashboard() {
  const { currentUser } = useContext(AuthContext);
  let userId = currentUser.uid;

  const handleSignout = () => {
    getAuth(app).signOut(app).then(() => {
      // console.log("Signout")
    }).catch((error) => {
      // console.log(error)
    });
  }

  const [sidebarOpen, setSidebarOpen] = useState(false)

  // show video
  const [showVideo, setShowVideo] = useState(false);
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);

  // step 1
  const [processedDocuments, setProcessedDocuments] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);

  // step 2
  const [extractionsChartData, setExtractionsChartData] = useState([]);

  // step 3 (a map of each language used in the extractions and the number of extraction that use it)
  const [languagesChartData, setLanguagesChartData] = useState([]);

  // step 4
  const [timeSaved, setTimeSaved] = useState(0);
  const [costSavings, setCostSavings] = useState(0);

  // Document Classification metrics
  const [totalClassifiedDocuments, setTotalClassifiedDocuments] = useState(0);
  const [documentTypesCount, setDocumentTypesCount] = useState(0);
  const [isLoadingExtractionsDashboard, setIsLoadingExtractionsDashboard] = useState(true);
  const [isLoadingClassificationsDashboard, setIsLoadingClassificationsDashboard] = useState(true);

  // calculate dashboard
  const calculateDashboard = (extractions) => {
    let totalDocuments = 0;
    let processedDocuments = 0;
    let pendingDocuments = 0;

    let extractionList = [];

    let languageCounts = {};

    for (const extractionId in extractions) {
      const extraction = extractions[extractionId];

      // step 1
      let localDocuments = 0;
      let localProcessedDocuments = 0;
      let localPendingDocuments = 0;

      const batches = extraction.batches || {};
      for (const batchId in batches) {
        const batch = batches[batchId];

        let numberOfDocs;
        try {
          numberOfDocs = batch.files.length;
        } catch (error) {
          numberOfDocs = 0;
        }

        localDocuments += numberOfDocs;
        if (batch.status !== "processing") {
          localProcessedDocuments += numberOfDocs;
        } else {
          localPendingDocuments += numberOfDocs;
        }
      }

      totalDocuments += localDocuments;
      processedDocuments += localProcessedDocuments;
      pendingDocuments += localPendingDocuments;

      // step 2
      extractionList.push({
        id: extractionId,
        name: extraction.name,
        documents: localDocuments,
        processedDocuments: localProcessedDocuments,
        pendingDocuments: localPendingDocuments,
      });

      // step 3
      let localLanguage = extraction.language;
      if (localLanguage) {
        if (!languageCounts[localLanguage]) {
          languageCounts[localLanguage] = localDocuments;
        } else {
          languageCounts[localLanguage] += localDocuments;
        }
      }
    }

    // step 2
    extractionList.forEach((extraction) => {
      extraction.percentage = totalDocuments > 0 ? Math.round((extraction.documents / totalDocuments) * 100) : 0;
    });

    // step 3
    let languagesArray = [];
    for (const lang in languageCounts) {
      languagesArray.push({
        language: lang,
        count: languageCounts[lang]
      });
    }

    const languagesChartData = languagesArray.filter(item => item.language !== "").map(item => {
      if (item.language === "") {
        item.language = "Unknown";
      }

      return ({
        name: item.language,
        "Documents with this language": item.count,
      })
    });
    languagesChartData.sort((a, b) => b["Documents with this language"] - a["Documents with this language"]);

    setLanguagesChartData(languagesChartData);

    let topThreeExtractions = extractionList.slice(0, 3); // Take the first 3 extractions
    let remainingExtractions = extractionList.slice(3); // Get the remaining extractions
    let otherDocuments = remainingExtractions.reduce((total, item) => total + item.documents, 0); // Sum of documents for the remaining extractions

    const extractionsChartData = [...topThreeExtractions, { name: "Others", documents: otherDocuments, percentage: totalDocuments > 0 ? Math.round((otherDocuments / totalDocuments) * 100) : 0 }];
    extractionsChartData.sort((a, b) => b.documents - a.documents);

    setExtractionsChartData(extractionsChartData);

    setTotalDocuments(totalDocuments);
    setProcessedDocuments(processedDocuments);
    setPendingDocuments(pendingDocuments);

    let timeSaved = totalDocuments * 3.25;
    timeSaved = Math.round(timeSaved);
    let costSavings = totalDocuments * 0.5;
    costSavings = Math.round(costSavings);

    setTimeSaved(timeSaved);
    setCostSavings(costSavings);
  }

  // calculate document classification metrics
  const calculateDashboardDocumentClassification = (classifications) => {
    let totalClassifiedDocuments = 0;
    let documentTypesCount = 0;

    // each classification has a batches object. each batch has a files array and thats how you find the number of classified documents.
    for (const classificationId in classifications) {
      const classification = classifications[classificationId];
      const batches = classification.batches || {};
      for (const batchId in batches) {
        const batch = batches[batchId];
        const files = batch.files || [];
        totalClassifiedDocuments += files.length;
      }

      // each classification has a documentTypes array that contains the possible document types
      if (classification.documentTypes && Array.isArray(classification.documentTypes)) {
        documentTypesCount += classification.documentTypes.length;
      }
    }

    setTotalClassifiedDocuments(totalClassifiedDocuments);
    setDocumentTypesCount(documentTypesCount);
  }

  // fetch data
  const fetchAllExtractions = async () => {
    setIsLoadingExtractionsDashboard(true);
    const databaseRef = ref(db, 'extractions/' + userId);
    const batchesRef = ref(db, 'batches/' + userId);

    try {
      // Fetch extractions and batches concurrently
      const [extractionsSnapshot, batchesSnapshot] = await Promise.all([
        get(databaseRef),
        get(batchesRef)
      ]);

      if (extractionsSnapshot.exists()) {
        const data = extractionsSnapshot.val();
        const batches = batchesSnapshot.exists() ? batchesSnapshot.val() : {};

        // Update each extraction with its corresponding batches
        for (const extractionId of Object.keys(data)) {
          data[extractionId].batches = batches[extractionId] || {};
        }

        calculateDashboard(data);
      } else {
        calculateDashboard({});
      }
    } catch (error) {
      // console.error("Error fetching data:", error);
      calculateDashboard({});
    } finally {
      setIsLoadingExtractionsDashboard(false);
    }
  }

  // fetch data for document classification
  const fetchDocumentClassificationData = async () => {
    setIsLoadingClassificationsDashboard(true);
    const databaseRef = ref(db, 'document_classification/classifications/' + userId);
    const batchesRef = ref(db, 'document_classification/batches/' + userId);

    try {
      const [classificationsSnapshot, batchesSnapshot] = await Promise.all([
        get(databaseRef),
        get(batchesRef)
      ]);

      if (classificationsSnapshot.exists()) {
        const data = classificationsSnapshot.val();
        const batches = batchesSnapshot.exists() ? batchesSnapshot.val() : {};

        // Update each classification with its corresponding batches
        for (const classificationId in data) {
          data[classificationId].batches = batches[classificationId] || {};
        }

        calculateDashboardDocumentClassification(data);
      } else {
        calculateDashboardDocumentClassification({});
      }

    } catch (error) {
      // console.error("Error fetching data:", error);
      calculateDashboardDocumentClassification({});
    } finally {
      setIsLoadingClassificationsDashboard(false);
    }
  }

  // update account object
  const updateAccountObject = async () => {
    // Reference to the user's database path
    const databaseRef = ref(db, 'userDetails/' + userId + '/account');

    const updatedUserDetails = {
      welcomeVideo: true,
    };

    // Set the data in the database
    try {
      await update(databaseRef, updatedUserDetails);
    } catch (error) {
      console.error("Error updating data:", error);
    }
  }

  // fetch user details
  const fetchUserDetails = async () => {
    const databaseRef = ref(db, 'userDetails/' + userId + '/account');

    try {
      const snapshot = await get(databaseRef);

      if (snapshot.exists()) {
        const data = snapshot.val();

        let welcomeVideo = data.welcomeVideo;
        if (welcomeVideo !== undefined && welcomeVideo !== null) {
          setShowVideo(false);
          setShowWelcomeToast(true);
        } else {
          setShowVideo(true);
          updateAccountObject();
        }
      } else {
        console.log("No data available");
        setShowVideo(true);
      }
    } catch (error) {
      // console.error("Error fetching data:", error);
    }
  }

  useEffect(() => {
    const appName = process.env.REACT_APP_NAME;
    document.title = "Dashboard - " + appName;

    const fetchData = async () => {
      await fetchAllExtractions();
      await fetchDocumentClassificationData();
      await fetchUserDetails();

      if (currentUser) {
        // We can add a check to see if we've already identified this user in this session
        if (!window.userIdentified) {
          posthog.identify(currentUser.uid, {
            email: currentUser.email,
            userId: currentUser.uid,
          });
          // Set a flag to avoid redundant identify calls in the same session
          window.userIdentified = true;
        }
      }
    }
    fetchData();
  }, [userId, currentUser]);

  return (
    <>
      <div>
        <MenuBar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} handleSignout={handleSignout} currentHref="dashboard" />

        <main className="lg:pl-72">
          <TopBar setSidebarOpen={setSidebarOpen} handleSignout={handleSignout} />

          <div className="py-10 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col justify-center items-start mb-10">
              <div className='px-3'>
                <a href='/dashboard' className="text-4xl font-bold text-gray-900">Dashboard</a>
              </div>

              {showWelcomeToast && (
                <ShowWelcomeMessage setShowWelcomeToast={setShowWelcomeToast} setShowVideo={setShowVideo} />
              )}

              <ModalForYoutubeVideo open={showVideo} setOpen={setShowVideo} >
                <ContainerForYoutubeVideo
                  closeModal={() => {
                    setShowVideo(false);
                    setShowWelcomeToast(true);
                  }}
                  updateAccountObject={updateAccountObject}
                />
              </ModalForYoutubeVideo>

              <div className='w-full px-3 mt-5 mb-50'>
                {/* Main Stats Row */}
                <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {/* Data Extraction Stats */}
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6">
                    <div className="flex items-center justify-between mb-2">
                      <dt className="truncate text-sm font-medium text-gray-500">Total documents</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Data Extraction
                      </span>
                    </div>
                    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                      {isLoadingExtractionsDashboard ? <DevSkeleton width={64} height={36} /> : totalDocuments}
                    </dd>
                  </div>

                  {/* Document Classification Stats */}
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6">
                    <div className="flex items-center justify-between mb-2">
                      <dt className="truncate text-sm font-medium text-gray-500">Classified documents</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        Classification
                      </span>
                    </div>
                    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                      {isLoadingClassificationsDashboard ? <DevSkeleton width={64} height={36} /> : totalClassifiedDocuments}
                    </dd>
                  </div>

                  {/* <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6">
                    <div className="flex items-center justify-between mb-2">
                      <dt className="truncate text-sm font-medium text-gray-500">Accuracy</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        Classification
                      </span>
                    </div>
                    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{classificationAccuracy}%</dd>
                  </div> */}

                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6">
                    <div className="flex items-center justify-between mb-2">
                      <dt className="truncate text-sm font-medium text-gray-500">Document types</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        Classification
                      </span>
                    </div>
                    <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
                      {isLoadingClassificationsDashboard ? <DevSkeleton width={64} height={36} /> : documentTypesCount}
                    </dd>
                  </div>
                </dl>

                {/* Charts and Details Row */}
                <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {/* Extractions Chart - Large */}
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6 sm:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Extractions</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Data Extraction
                      </span>
                    </div>
                    {isLoadingExtractionsDashboard ? (
                      <DashboardChartSkeleton />
                    ) : (
                      <div className="flex flex-col sm:flex-row mt-6 h-50">
                        <DonutChartUsageExample2 newChartDataModel={extractionsChartData} className="h-50" />
                        <div className="w-full sm:w-2/3 flex flex-col justify-center">
                          <DonutTable newChartDataModel={extractionsChartData} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Performance - Data Extraction Only */}
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border shadow-sm sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Performance</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Data Extraction
                      </span>
                    </div>
                    <div className="mt-4 space-y-6">
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Time saved</div>
                        <div className="text-2xl font-semibold text-gray-900">
                          {isLoadingExtractionsDashboard ? <DevSkeleton width={96} height={28} /> : `${timeSaved} min`}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Cost savings</div>
                        <div className="text-2xl font-semibold text-gray-900">
                          {isLoadingExtractionsDashboard ? <DevSkeleton width={96} height={28} /> : `$${costSavings}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </dl>

                {/* Languages Chart - Smaller, Data Extraction Only */}
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Languages Distribution</dt>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Data Extraction
                      </span>
                    </div>
                    <div className="h-80">
                      {isLoadingExtractionsDashboard ? <DevSkeleton height={280} /> : <BarChartCard newChartDataModel={languagesChartData} />}
                    </div>
                  </div>

                  {/* Placeholder for future feature or additional chart */}
                  <div className="overflow-hidden rounded-lg bg-white px-4 py-5 border sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <dt className="truncate text-sm font-medium text-gray-500">Quick Actions</dt>
                    </div>
                    <div className="mt-4 space-y-3">
                      <a href="/extractions" className="w-full text-center px-4 py-3 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 block font-medium">
                        View All Extractions
                      </a>
                      <a href="/classifications" className="w-full text-center px-4 py-3 text-sm bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 block font-medium">
                        View All Classifications
                      </a>
                      <a href="/api" className="w-full text-center px-4 py-3 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 block font-medium">
                        Manage API Key
                      </a>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

function ShowWelcomeMessage({ setShowWelcomeToast, setShowVideo }) {
  return (
    <>
      <div className="z-100 pointer-events-none fixed inset-x-0 bottom-0 sm:flex sm:justify-center sm:px-6 sm:pb-5 lg:px-8">
        <div className="pointer-events-auto flex items-center justify-between gap-x-6 bg-gray-900 px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5">
          <p className="text-sm leading-6 text-white">
            <button
              onClick={() => {
                setShowWelcomeToast(false);
                setShowVideo(true);
              }}
            >
              <strong className="font-semibold">Welcome to Extracta LABS!</strong>
              <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
                <circle cx={1} cy={1} r={1} />
              </svg>
              Streamline your workflow with automated data extraction
              <svg viewBox="0 0 2 2" className="mx-2 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
                <circle cx={1} cy={1} r={1} />
              </svg>
              Watch video&nbsp;<span aria-hidden="true">&rarr;</span>
            </button>
          </p>
          <button
            type="button"
            className="-m-1.5 flex-none p-1.5"
            onClick={() => {
              setShowWelcomeToast(false);
            }}
          >
            <span className="sr-only">Dismiss</span>
            <XMarkIcon className="h-5 w-5 text-white" aria-hidden="true" />
          </button>
        </div>
      </div>
    </>
  )
}

const ContainerForYoutubeVideo = ({ closeModal, updateAccountObject }) => {
  const iframeRef = useRef(null);

  const toggleFullscreen = () => {
    if (iframeRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        iframeRef.current.requestFullscreen().catch(e => {
          console.log("Error attempting to enable full-screen mode:", e);
        });
      }
    }
  };

  const skipFunction = () => {
    updateAccountObject();
    closeModal();
  }

  const watchFunction = () => {
    updateAccountObject();
    toggleFullscreen();
  }

  return (
    <div className="modal-container" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      padding: '20px 20px 20px 20px', // Space between the border and content
    }}>
      <h1 className="mt-2 text-2xl font-semibold text-gray-900">
        Welcome to Extracta LABS! 🚀
      </h1>
      {/* add secondary small text */}
      <p className="mb-3.5 text-base text-gray-500">
        Streamline your workflow with automated data extraction. Watch the video below to get started.
      </p>
      <iframe
        ref={iframeRef}
        width="100%"
        height="95%" // Adjust the height based on your requirements
        src="https://www.youtube.com/embed/D_Jpj2Ab1fw?autoplay=1&controls=1"
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
        title="Welcome Video - Extracta LABS"
        style={{ marginBottom: '10px', borderRadius: '5px' }}
      ></iframe>
      <div className="mt-4 flex items-center justify-end">
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
          onClick={() => skipFunction()}
        >
          Skip
        </button>
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
          onClick={() => watchFunction()}
        >
          <ArrowRightIcon className="-ml-0.5 mr-1 h-5 w-5" aria-hidden="true" />
          Watch Video
        </button>
      </div>
    </div>
  );
}

function DashboardChartSkeleton() {
  return (
    <div className="flex flex-col sm:flex-row mt-6 h-50 gap-6">
      <div className="w-full sm:w-1/3 flex items-center justify-center">
        <DevSkeleton circle width={160} height={160} />
      </div>
      <div className="w-full sm:w-2/3 flex flex-col justify-center space-y-3">
        <DevSkeleton height={20} />
        <DevSkeleton height={20} />
        <DevSkeleton height={20} />
        <DevSkeleton height={20} />
      </div>
    </div>
  );
}

function BarChartCard({ newChartDataModel }) {
  return (
    <BarChart
      className="mt-6"
      data={newChartDataModel}
      index="name"
      categories={["Documents with this language"]}
      colors={["indigo"]}
      yAxisWidth={48}
      showAnimation={true}
    />
  )
}

const donutColors = ["indigo", "rose", "emerald", "orange"];

const DonutChartUsageExample2 = ({ newChartDataModel }) => {
  return (
    <>
      <DonutChart
        className="w-full sm:w-1/3 h-48 m-auto"
        data={newChartDataModel}
        category="documents"
        index="name"
        colors={donutColors}
        variant="pie"
        showAnimation={true}
      />
    </>
  );
};

const DonutTable = ({ newChartDataModel }) => {
  return (
    <Table>
      <TableBody>
        {newChartDataModel.map((item, index) => (
          <TableRow key={index}>
            <TableCell className="flex flex-row items-center">
              <div className={classNames("mr-2 h-3 w-3 rounded-full", "bg-" + donutColors[index] + "-500")}></div>
              {item.name ?? '-'}
            </TableCell>
            <TableCell>
              <Text>{item.documents} documents</Text>
            </TableCell>
            <TableCell>
              <Text>{item.percentage}%</Text>
            </TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell className="font-bold">
            Total
          </TableCell>
          <TableCell>
            <Text className="font-bold">{newChartDataModel.reduce((total, item) => total + item.documents, 0)} documents</Text>
          </TableCell>
          <TableCell>
            <Text className="font-bold">100%</Text>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}